// ─── useSOCCache.js ───────────────────────────────────────────────────────────
// Persistent cache for Analytics + Incidents data.
// Survives sign-out/sign-in so data appears instantly on re-login.
// Uses sessionStorage (tab-scoped) — data stays while browser tab is open.
// Both analyst and admin see the same cached data.
//
// USAGE in Analytics.jsx — replace the existing fetchData useEffect:
//   import { useSOCCache } from '../../hooks/useSOCCache';
//   const { alerts, metrics, loading } = useSOCCache();
//
// USAGE in Incidents.jsx — replace the existing load() fetch:
//   import { useSOCCache } from '../../hooks/useSOCCache';
//   const { incidents, loading } = useSOCCache();
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from 'react';

const API           = '/api';
const CACHE_TTL_MS  = 5 * 60 * 1000; // 5 minutes — refresh if stale
const CACHE_KEYS    = {
  metrics:   'aegis_cache_metrics',
  alerts:    'aegis_cache_alerts',
  incidents: 'aegis_cache_incidents',
  ts:        'aegis_cache_timestamp',
};

// ── Low-level cache helpers ───────────────────────────────────────────────────
function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeCache(key, data) {
  try { sessionStorage.setItem(key, JSON.stringify(data)); } catch {}
}

function cacheAge() {
  const ts = readCache(CACHE_KEYS.ts);
  return ts ? Date.now() - ts : Infinity;
}

function isCacheValid() {
  return cacheAge() < CACHE_TTL_MS && readCache(CACHE_KEYS.metrics) !== null;
}

// ── Main hook ─────────────────────────────────────────────────────────────────
export function useSOCCache() {
  const [metrics,   setMetrics]   = useState(() => readCache(CACHE_KEYS.metrics)   || null);
  const [alerts,    setAlerts]    = useState(() => readCache(CACHE_KEYS.alerts)     || []);
  const [incidents, setIncidents] = useState(() => readCache(CACHE_KEYS.incidents)  || []);
  const [loading,   setLoading]   = useState(!isCacheValid()); // skip loading if cache hit
  const fetchingRef = useRef(false);

  const fetchAll = useCallback(async (force = false) => {
    if (fetchingRef.current) return;
    if (!force && isCacheValid()) {
      // Cache is fresh — use it, no network call
      setMetrics(readCache(CACHE_KEYS.metrics));
      setAlerts(readCache(CACHE_KEYS.alerts)     || []);
      setIncidents(readCache(CACHE_KEYS.incidents) || []);
      setLoading(false);
      return;
    }

    fetchingRef.current = true;
    setLoading(true);

    try {
      // Parallel fetch — all 3 at once
      const [mRes, aRes, iRes] = await Promise.all([
        fetch(`${API}/metrics`).then(r => r.json()),
        fetch(`${API}/alerts?limit=5000`).then(r => r.json()),
        fetch(`${API}/incidents`).then(r => r.json()),
      ]);

      const metricsData   = mRes;
      const alertsData    = aRes.alerts    || [];
      const incidentsData = (iRes.incidents || []).map(normalizeIncident);

      // Update state
      setMetrics(metricsData);
      setAlerts(alertsData);
      setIncidents(incidentsData);

      // Write to cache
      writeCache(CACHE_KEYS.metrics,   metricsData);
      writeCache(CACHE_KEYS.alerts,    alertsData);
      writeCache(CACHE_KEYS.incidents, incidentsData);
      writeCache(CACHE_KEYS.ts,        Date.now());

    } catch (e) {
      console.warn('SOC cache fetch failed:', e);
      // Keep showing stale cache data — better than empty
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchAll();
    // Background refresh every 30 seconds
    const interval = setInterval(() => fetchAll(true), 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  return { metrics, alerts, incidents, loading, refresh: () => fetchAll(true) };
}

// ── Incident normalizer (same as in Incidents.jsx) ────────────────────────────
function normalizeIncident(inc) {
  return {
    ...inc,
    id:  inc.id  || inc.incident_id || '',
    ips: (inc.ips && inc.ips.length) ? inc.ips : (inc.src_ips || inc.source_ips || []),
  };
}

// ── Cache invalidation (call after Upload/Detect completes) ───────────────────
export function invalidateSOCCache() {
  Object.values(CACHE_KEYS).forEach(k => {
    try { sessionStorage.removeItem(k); } catch {}
  });
}

// ── Manual cache clear (for debug / sign-out scenarios) ───────────────────────
export function clearSOCCache() {
  invalidateSOCCache();
}
